import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { useToast } from '../ui/ToastProvider'
import MenuGuideButton from '../components/MenuGuideButton'

function groupLabel(code) {
  if (code === 'GA') return 'GA'
  if (code === 'INSURER') return '원수사'
  if (code === 'SYSTEM') return '시스템'
  return code || '-'
}

export default function PermissionsAdminPage() {
  const { show } = useToast()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [roles, setRoles] = useState([])
  const [permissions, setPermissions] = useState([])

  // 역할별 권한 탭 (요구사항: GA/원수사)
  const [group, setGroup] = useState('GA')
  const [roleId, setRoleId] = useState('')
  const [permQ, setPermQ] = useState('')
  const [selectedCodes, setSelectedCodes] = useState(new Set())
  const [saving, setSaving] = useState(false)

  // 권한 CRUD
  const [newCode, setNewCode] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [edit, setEdit] = useState({ code: '', labelKo: '' })

  const loadAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const [r1, r2] = await Promise.all([
        api.get('/rbac/roles'),
        api.get('/rbac/permissions'),
      ])
      const rolesPayload = (r1?.data && r1.data.data !== undefined) ? r1.data.data : r1.data
      const permsPayload = (r2?.data && r2.data.data !== undefined) ? r2.data.data : r2.data
      setRoles(Array.isArray(rolesPayload) ? rolesPayload : [])
      setPermissions(Array.isArray(permsPayload) ? permsPayload : [])
    } catch (e) {
      setError('권한 정보를 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void loadAll() }, [])

  const rolesByGroup = useMemo(() => {
    const list = Array.isArray(roles) ? roles : []
    return list
      .filter(r => (r?.groupCode || '') === group)
      .sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')))
  }, [roles, group])

  // 선택된 roleId가 비었으면, 그룹 내 첫 role로 자동 선택
  useEffect(() => {
    const first = rolesByGroup[0]
    if (!roleId && first?.id != null) {
      setRoleId(String(first.id))
    }
    // roleId가 그룹 변경으로 유효하지 않으면 첫 role로 교체
    if (roleId && !rolesByGroup.some(r => String(r?.id) === String(roleId))) {
      if (first?.id != null) setRoleId(String(first.id))
      else setRoleId('')
    }
  }, [group, rolesByGroup, roleId])

  const selectedRole = useMemo(() => roles.find(r => String(r?.id) === String(roleId)) || null, [roles, roleId])

  // role 변경 시 선택 권한 로드
  useEffect(() => {
    const codes = selectedRole?.permissionCodes || []
    setSelectedCodes(new Set(Array.isArray(codes) ? codes.map(String) : []))
  }, [selectedRole?.id, selectedRole?.permissionCodes])

  const filteredPermissions = useMemo(() => {
    const q = (permQ || '').trim().toLowerCase()
    const list = Array.isArray(permissions) ? permissions : []
    const sorted = [...list].sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')))
    if (!q) return sorted
    return sorted.filter(p => {
      const code = String(p?.code || '').toLowerCase()
      const label = String(p?.labelKo || '').toLowerCase()
      return code.includes(q) || label.includes(q)
    })
  }, [permissions, permQ])

  const toggle = (code) => {
    const c = String(code || '')
    if (!c) return
    setSelectedCodes(prev => {
      const next = new Set(prev)
      if (next.has(c)) next.delete(c)
      else next.add(c)
      return next
    })
  }

  const saveRolePerms = async () => {
    if (!roleId) return
    setSaving(true)
    try {
      const codes = Array.from(selectedCodes)
      await api.put(`/rbac/roles/${roleId}/permissions`, { permissionCodes: codes })
      show('권한이 저장되었습니다', 'success')
      await loadAll()
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error?.message || e?.message
      show(msg || '저장 실패', 'error')
    } finally {
      setSaving(false)
    }
  }

  const createPermission = async (e) => {
    e.preventDefault()
    const code = String(newCode || '').trim()
    const labelKo = String(newLabel || '').trim()
    if (!code || !labelKo) return show('권한 코드/라벨을 입력해주세요', 'error')
    try {
      await api.post('/rbac/permissions', { code, labelKo })
      setNewCode('')
      setNewLabel('')
      show('권한이 추가되었습니다', 'success')
      await loadAll()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message
      show(msg || '권한 추가 실패', 'error')
    }
  }

  const startEdit = (p) => {
    setEdit({ code: String(p?.code || ''), labelKo: String(p?.labelKo || '') })
  }

  const saveEdit = async () => {
    if (!edit.code) return
    const labelKo = String(edit.labelKo || '').trim()
    if (!labelKo) return show('라벨을 입력해주세요', 'error')
    try {
      await api.patch(`/rbac/permissions/${encodeURIComponent(edit.code)}`, { labelKo })
      show('권한이 수정되었습니다', 'success')
      setEdit({ code: '', labelKo: '' })
      await loadAll()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message
      show(msg || '권한 수정 실패', 'error')
    }
  }

  const deletePermission = async (code) => {
    const c = String(code || '')
    if (!c) return
    if (!window.confirm(`권한 "${c}"를 삭제할까요?\n- 해당 권한을 가진 인원이 있으면 삭제가 차단됩니다.`)) return
    try {
      await api.delete(`/rbac/permissions/${encodeURIComponent(c)}`)
      show('권한이 삭제되었습니다', 'success')
      await loadAll()
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message
      show(msg || '권한 삭제 실패', 'error')
    }
  }

  if (loading) return <Loading />
  if (error) return <InlineError message={error} />

  return (
    <section className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold">권한관리</h1>
          <MenuGuideButton slug="data-management" title="데이터관리 가이드" />
        </div>
      </div>

      {/* 역할별 권한 */}
      <div className="rounded border bg-white p-4 space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold">역할별 권한</div>
            <div className="text-xs text-slate-500 mt-1">GA/원수사 그룹을 선택한 뒤 역할별 권한을 수정하세요.</div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className={`rounded border px-3 py-1 text-sm ${group === 'GA' ? 'bg-slate-50' : ''}`} onClick={() => setGroup('GA')}>GA</button>
            <button type="button" className={`rounded border px-3 py-1 text-sm ${group === 'INSURER' ? 'bg-slate-50' : ''}`} onClick={() => setGroup('INSURER')}>원수사</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-2">
            <label className="block text-sm text-slate-600">역할 선택</label>
            <select className="w-full rounded border px-3 py-2" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              {rolesByGroup.length === 0 && <option value="">역할 없음</option>}
              {rolesByGroup.map(r => (
                <option key={r.id} value={String(r.id)}>
                  {r.labelKo || r.code} ({r.code})
                </option>
              ))}
            </select>
            {selectedRole && (
              <div className="text-xs text-slate-500">
                그룹: <span className="font-medium">{groupLabel(selectedRole.groupCode)}</span>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <input
                className="rounded border px-3 py-2 text-sm flex-1 min-w-[240px]"
                placeholder="권한 검색(코드/라벨)"
                value={permQ}
                onChange={(e) => setPermQ(e.target.value)}
              />
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60"
                disabled={!roleId || saving}
                onClick={saveRolePerms}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>

            <div className="max-h-[420px] overflow-auto rounded border">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-3 py-2 w-[60px]">선택</th>
                    <th className="px-3 py-2">권한</th>
                    <th className="px-3 py-2">코드</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPermissions.length === 0 && (
                    <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={3}>권한이 없습니다</td></tr>
                  )}
                  {filteredPermissions.map(p => {
                    const code = String(p.code)
                    const checked = selectedCodes.has(code)
                    return (
                      <tr key={code} className="border-t">
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={checked} onChange={() => toggle(code)} disabled={!roleId} />
                        </td>
                        <td className="px-3 py-2">{p.labelKo}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{code}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-500">
              선택된 권한 수: <span className="font-medium">{selectedCodes.size}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 권한(사전) CRUD */}
      <div className="rounded border bg-white p-4 space-y-4">
        <div>
          <div className="font-semibold">권한 목록(사전)</div>
          <div className="text-xs text-slate-500 mt-1">권한 코드/라벨을 추가/수정/삭제합니다.</div>
        </div>

        <form onSubmit={createPermission} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <label className="block text-sm text-slate-600">권한 코드</label>
            <input className="mt-1 w-full rounded border px-3 py-2 font-mono text-sm" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="예: ORG_MANAGE" />
          </div>
          <div>
            <label className="block text-sm text-slate-600">권한 라벨(한글)</label>
            <input className="mt-1 w-full rounded border px-3 py-2 text-sm" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="예: 조직 관리" />
          </div>
          <button type="submit" className="rounded border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-800 hover:bg-teal-100">추가</button>
        </form>

        <div className="overflow-x-auto rounded border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">코드</th>
                <th className="px-3 py-2">라벨</th>
                <th className="px-3 py-2 w-[220px]">관리</th>
              </tr>
            </thead>
            <tbody>
              {permissions.length === 0 && (
                <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={3}>권한이 없습니다</td></tr>
              )}
              {permissions
                .slice()
                .sort((a, b) => String(a?.code || '').localeCompare(String(b?.code || '')))
                .map(p => (
                  <tr key={p.code} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{p.code}</td>
                    <td className="px-3 py-2">
                      {edit.code === p.code ? (
                        <input className="w-full rounded border px-3 py-1 text-sm" value={edit.labelKo} onChange={(e) => setEdit(prev => ({ ...prev, labelKo: e.target.value }))} />
                      ) : (
                        p.labelKo
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {edit.code === p.code ? (
                        <div className="flex gap-2">
                          <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => { setEdit({ code: '', labelKo: '' }) }}>취소</button>
                          <button type="button" className="rounded bg-brand-navy text-white px-3 py-1 text-sm" onClick={saveEdit}>저장</button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={() => startEdit(p)}>수정</button>
                          <button type="button" className="rounded border border-red-200 text-red-600 px-3 py-1 text-sm hover:bg-red-50" onClick={() => deletePermission(p.code)}>삭제</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-500">
          삭제는 “해당 권한을 가진 인원(역할을 통해 권한이 부여된 사용자)”이 있으면 차단됩니다.
        </div>
      </div>
    </section>
  )
}
